import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grow } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { lookupVendorMakes } from "services/vendorMakeService";

const getSelectedVendorType = (appliedFilters = {}) =>
  Array.isArray(appliedFilters.category) && appliedFilters.category.length > 0
    ? appliedFilters.category[0]
    : "";

const getSelectedTaxonomyId = (appliedFilters = {}) =>
  Array.isArray(appliedFilters.vendorCategory) &&
  appliedFilters.vendorCategory.length > 0
    ? appliedFilters.vendorCategory[0]
    : "";

const getSelectedMakeId = (appliedFilters = {}) =>
  Array.isArray(appliedFilters.vendorMake) && appliedFilters.vendorMake.length > 0
    ? appliedFilters.vendorMake[0]
    : "";

const normalizeSearch = (value = "") => value.toString().trim().toLowerCase();

const VendorCategoryQuickFilter = ({
  taxonomyAll = {},
  appliedFilters = {},
  applyFilterData,
  clearAllVendorCategoryFilters,
  vendorTypeCounts = {},
  disciplineCounts = {},
  categoryCounts = {},
  subCategoryCounts = {},
  makeCounts = {},
  makeCountsBySubCategory = {},
  totalCount = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [panelOriginX, setPanelOriginX] = useState(40);
  const rootRef = useRef(null);
  const vendorTypeButtonRefs = useRef({});
  const [makeRetry, setMakeRetry] = useState(0);
  const [makeLookup, setMakeLookup] = useState({
    subCategoryId: "",
    status: "idle",
    options: [],
  });
  const [searchMakeLookup, setSearchMakeLookup] = useState({
    key: "",
    status: "idle",
    bySubCategory: {},
  });

  const vendorTypes = useMemo(() => Object.keys(taxonomyAll), [taxonomyAll]);
  const selectedVendorType = getSelectedVendorType(appliedFilters);
  const selectedTaxonomyId = getSelectedTaxonomyId(appliedFilters);
  const selectedMakeId = getSelectedMakeId(appliedFilters);
  const normalizedSearchText = normalizeSearch(searchText);
  const hasSearchText = Boolean(normalizedSearchText);

  const taxonomyIndex = useMemo(() => {
    const index = {
      disciplines: {},
      categories: {},
      subCategories: {},
    };

    Object.entries(taxonomyAll).forEach(([vendorType, data]) => {
      (data?.disciplines || []).forEach((discipline) => {
        index.disciplines[discipline._id] = {
          ...discipline,
          vendorType,
        };

        (discipline.categories || []).forEach((category) => {
          index.categories[category._id] = {
            ...category,
            vendorType,
            disciplineId: discipline._id,
            disciplineName: discipline.name,
          };

          (category.subCategories || []).forEach((subCategory) => {
            index.subCategories[subCategory._id] = {
              ...subCategory,
              vendorType,
              disciplineId: discipline._id,
              disciplineName: discipline.name,
              categoryId: category._id,
              categoryName: category.name,
            };
          });
        });
      });
    });

    return index;
  }, [taxonomyAll]);

  const selectedDisciplineId = useMemo(() => {
    if (taxonomyIndex.disciplines[selectedTaxonomyId]) {
      return selectedTaxonomyId;
    }

    if (taxonomyIndex.categories[selectedTaxonomyId]) {
      return taxonomyIndex.categories[selectedTaxonomyId].disciplineId;
    }

    if (taxonomyIndex.subCategories[selectedTaxonomyId]) {
      return taxonomyIndex.subCategories[selectedTaxonomyId].disciplineId;
    }

    return "";
  }, [selectedTaxonomyId, taxonomyIndex]);

  const selectedCategoryId = useMemo(() => {
    if (taxonomyIndex.categories[selectedTaxonomyId]) {
      return selectedTaxonomyId;
    }

    if (taxonomyIndex.subCategories[selectedTaxonomyId]) {
      return taxonomyIndex.subCategories[selectedTaxonomyId].categoryId;
    }

    return "";
  }, [selectedTaxonomyId, taxonomyIndex]);

  const selectedSubCategoryId = taxonomyIndex.subCategories[selectedTaxonomyId]
    ? selectedTaxonomyId
    : "";

  const materialSubCategoryIds = useMemo(
    () =>
      (taxonomyAll?.Material?.disciplines || []).flatMap((discipline) =>
        (discipline?.categories || []).flatMap((category) =>
          (category?.subCategories || []).map((subCategory) =>
            String(subCategory?._id || ""),
          ),
        ),
      ).filter(Boolean),
    [taxonomyAll],
  );
  const materialSubCategoryKey = materialSubCategoryIds.join("|");

  useEffect(() => {
    const subCategoryId = String(selectedSubCategoryId || "");

    if (selectedVendorType !== "Material" || !subCategoryId) {
      setMakeLookup({ subCategoryId: "", status: "idle", options: [] });
      return undefined;
    }

    const controller = new AbortController();
    setMakeLookup({ subCategoryId, status: "loading", options: [] });

    lookupVendorMakes([subCategoryId], [], controller.signal)
      .then((data) => {
        setMakeLookup({
          subCategoryId,
          status: "success",
          options: Array.isArray(data?.bySubCategory?.[subCategoryId])
            ? data.bySubCategory[subCategoryId]
            : [],
        });
      })
      .catch((error) => {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        setMakeLookup({ subCategoryId, status: "error", options: [] });
      });

    return () => controller.abort();
  }, [makeRetry, selectedSubCategoryId, selectedVendorType]);

  useEffect(() => {
    if (
      selectedVendorType !== "Material" ||
      !hasSearchText ||
      !materialSubCategoryIds.length
    ) {
      return undefined;
    }

    const controller = new AbortController();
    setSearchMakeLookup({
      key: materialSubCategoryKey,
      status: "loading",
      bySubCategory: {},
    });

    lookupVendorMakes(materialSubCategoryIds, [], controller.signal)
      .then((data) => {
        setSearchMakeLookup({
          key: materialSubCategoryKey,
          status: "success",
          bySubCategory: data?.bySubCategory || {},
        });
      })
      .catch((error) => {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        setSearchMakeLookup({
          key: materialSubCategoryKey,
          status: "error",
          bySubCategory: {},
        });
      });

    return () => controller.abort();
  }, [
    materialSubCategoryIds,
    materialSubCategoryKey,
    hasSearchText,
    selectedVendorType,
  ]);

  useEffect(() => {
    if (selectedVendorType) {
      setIsExpanded(true);
    }
  }, [selectedVendorType]);

  useEffect(() => {
    if (!selectedVendorType || !isExpanded) return undefined;

    const updatePanelOrigin = () => {
      const root = rootRef.current;
      const button = vendorTypeButtonRefs.current[selectedVendorType];
      if (!root || !button) return;

      const rootRect = root.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const buttonCenter =
        buttonRect.left - rootRect.left + buttonRect.width / 2;

      setPanelOriginX(
        Math.max(28, Math.min(rootRect.width - 28, buttonCenter)),
      );
    };

    const frameId = window.requestAnimationFrame(updatePanelOrigin);
    window.addEventListener("resize", updatePanelOrigin);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updatePanelOrigin);
    };
  }, [isExpanded, selectedVendorType]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isExpanded]);

  const matchingMakes = useMemo(() => {
    if (!normalizedSearchText || searchMakeLookup.status !== "success") {
      return [];
    }

    return Object.entries(searchMakeLookup.bySubCategory).flatMap(
      ([subCategoryId, makes]) => {
        const subCategory = taxonomyIndex.subCategories[subCategoryId];
        if (!subCategory) return [];

        return (makes || [])
          .filter((make) =>
            normalizeSearch(make?.name).includes(normalizedSearchText),
          )
          .map((make) => ({
            ...make,
            makeId: String(make?._id || ""),
            subCategoryId,
            subCategoryName: subCategory.name,
            categoryId: subCategory.categoryId,
            categoryName: subCategory.categoryName,
            disciplineId: subCategory.disciplineId,
            disciplineName: subCategory.disciplineName,
          }));
      },
    );
  }, [
    normalizedSearchText,
    searchMakeLookup.bySubCategory,
    searchMakeLookup.status,
    taxonomyIndex.subCategories,
  ]);

  const matchingMakePathIds = useMemo(
    () => ({
      disciplines: new Set(matchingMakes.map((make) => make.disciplineId)),
      categories: new Set(matchingMakes.map((make) => make.categoryId)),
      subCategories: new Set(
        matchingMakes.map((make) => make.subCategoryId),
      ),
    }),
    [matchingMakes],
  );

  const visibleDisciplines = useMemo(() => {
    if (!selectedVendorType || !taxonomyAll[selectedVendorType]) {
      return [];
    }

    const disciplines = taxonomyAll[selectedVendorType].disciplines || [];

    if (!normalizedSearchText) {
      return disciplines;
    }

    return disciplines.filter((discipline) => {
      const disciplineMatches = normalizeSearch(discipline?.name).includes(
        normalizedSearchText,
      );
      const categoryMatches = (discipline?.categories || []).some(
        (category) =>
          normalizeSearch(category?.name).includes(normalizedSearchText) ||
          (category?.subCategories || []).some((subCategory) =>
            normalizeSearch(subCategory?.name).includes(normalizedSearchText),
          ),
      );

      return (
        disciplineMatches ||
        categoryMatches ||
        matchingMakePathIds.disciplines.has(discipline?._id)
      );
    });
  }, [
    matchingMakePathIds.disciplines,
    normalizedSearchText,
    selectedVendorType,
    taxonomyAll,
  ]);

  const visibleCategories = useMemo(() => {
    if (!selectedVendorType || !taxonomyAll[selectedVendorType]) {
      return [];
    }

    if (normalizedSearchText) {
      return (taxonomyAll[selectedVendorType].disciplines || []).flatMap(
        (discipline) =>
          (discipline?.categories || []).filter(
            (category) =>
              normalizeSearch(category?.name).includes(normalizedSearchText) ||
              (category?.subCategories || []).some((subCategory) =>
                normalizeSearch(subCategory?.name).includes(normalizedSearchText),
              ) ||
              matchingMakePathIds.categories.has(category?._id),
          ),
      );
    }

    if (!selectedDisciplineId || !taxonomyIndex.disciplines[selectedDisciplineId]) {
      return [];
    }

    return taxonomyIndex.disciplines[selectedDisciplineId].categories || [];
  }, [
    normalizedSearchText,
    matchingMakePathIds.categories,
    selectedDisciplineId,
    selectedVendorType,
    taxonomyAll,
    taxonomyIndex,
  ]);

  const visibleSubCategories = useMemo(() => {
    if (!selectedVendorType || !taxonomyAll[selectedVendorType]) {
      return [];
    }

    if (normalizedSearchText) {
      return (taxonomyAll[selectedVendorType].disciplines || []).flatMap(
        (discipline) =>
          (discipline?.categories || []).flatMap((category) =>
            (category?.subCategories || [])
              .filter((subCategory) =>
                normalizeSearch(subCategory?.name).includes(normalizedSearchText) ||
                matchingMakePathIds.subCategories.has(subCategory?._id),
              )
              .map((subCategory) => ({
                ...subCategory,
                categoryId: category?._id,
                categoryName: category?.name,
                disciplineId: discipline?._id,
                disciplineName: discipline?.name,
              })),
          ),
      );
    }

    if (!selectedCategoryId || !taxonomyIndex.categories[selectedCategoryId]) {
      return [];
    }

    return taxonomyIndex.categories[selectedCategoryId].subCategories || [];
  }, [
    normalizedSearchText,
    matchingMakePathIds.subCategories,
    selectedCategoryId,
    selectedVendorType,
    taxonomyAll,
    taxonomyIndex,
  ]);

  const applyQuickFilter = ({
    vendorType = "",
    taxonomyId = "",
    makeId = "",
  }) => {
    applyFilterData({
      ...appliedFilters,
      category: vendorType ? [vendorType] : [],
      vendorCategory: taxonomyId ? [taxonomyId] : [],
      vendorMake: makeId ? [makeId] : [],
    });
  };

  const handleVendorTypeClick = (vendorType) => {
    const root = rootRef.current;
    const button = vendorTypeButtonRefs.current[vendorType];
    if (root && button) {
      const rootRect = root.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      setPanelOriginX(
        Math.max(
          28,
          Math.min(
            rootRect.width - 28,
            buttonRect.left - rootRect.left + buttonRect.width / 2,
          ),
        ),
      );
    }

    if (selectedVendorType === vendorType) {
      setIsExpanded((previous) => !previous);
      return;
    }

    setSearchText("");
    setIsExpanded(true);
    applyQuickFilter({ vendorType, taxonomyId: "" });
  };

  const handleDisciplineClick = (disciplineId) => {
    if (!selectedVendorType) return;

    if (selectedTaxonomyId === disciplineId) {
      applyQuickFilter({ vendorType: selectedVendorType, taxonomyId: "" });
      return;
    }

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: disciplineId,
    });
  };

  const handleCategoryClick = (categoryId) => {
    if (!selectedVendorType) return;

    if (selectedTaxonomyId === categoryId) {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedDisciplineId,
      });
      return;
    }

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: categoryId,
    });
  };

  const handleSubCategoryClick = (subCategoryId) => {
    if (!selectedVendorType) return;

    if (selectedTaxonomyId === subCategoryId) {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedCategoryId,
      });
      return;
    }

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: subCategoryId,
    });
  };

  const handleMakeClick = (makeId, subCategoryId = selectedSubCategoryId) => {
    if (!selectedVendorType || !subCategoryId) return;

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: subCategoryId,
      makeId:
        selectedMakeId === makeId && selectedSubCategoryId === subCategoryId
          ? ""
          : makeId,
    });
  };

  const selectedFilterChips = useMemo(() => {
    const chips = [];

    if (selectedVendorType) {
      chips.push({
        id: selectedVendorType,
        label: selectedVendorType,
        type: "vendorType",
      });
    }

    if (selectedDisciplineId && taxonomyIndex.disciplines[selectedDisciplineId]) {
      chips.push({
        id: selectedDisciplineId,
        label: taxonomyIndex.disciplines[selectedDisciplineId].name,
        type: "discipline",
      });
    }

    if (selectedCategoryId && taxonomyIndex.categories[selectedCategoryId]) {
      chips.push({
        id: selectedCategoryId,
        label: taxonomyIndex.categories[selectedCategoryId].name,
        type: "category",
      });
    }

    if (selectedSubCategoryId && taxonomyIndex.subCategories[selectedSubCategoryId]) {
      chips.push({
        id: selectedSubCategoryId,
        label: taxonomyIndex.subCategories[selectedSubCategoryId].name,
        type: "subCategory",
      });
    }

    if (selectedMakeId) {
      const selectedMake = makeLookup.options.find(
        (make) => String(make?._id) === String(selectedMakeId),
      ) || matchingMakes.find(
        (make) => String(make?.makeId) === String(selectedMakeId),
      );

      chips.push({
        id: selectedMakeId,
        label: selectedMake?.name || "Make / Brand",
        type: "make",
      });
    }

    return chips;
  }, [
    makeLookup.options,
    matchingMakes,
    selectedCategoryId,
    selectedDisciplineId,
    selectedMakeId,
    selectedSubCategoryId,
    selectedVendorType,
    taxonomyIndex,
  ]);

  const handleChipRemove = (chip) => {
    if (chip.type === "vendorType") {
      setIsExpanded(false);
      setSearchText("");
      clearAllVendorCategoryFilters();
      return;
    }

    if (chip.type === "discipline") {
      applyQuickFilter({ vendorType: selectedVendorType, taxonomyId: "" });
      return;
    }

    if (chip.type === "category") {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedDisciplineId,
      });
      return;
    }

    if (chip.type === "subCategory") {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedCategoryId,
      });
      return;
    }

    if (chip.type === "make") {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedSubCategoryId,
      });
    }
  };

  const showDetailPanels = Boolean(selectedVendorType) && isExpanded;
  const hasAppliedFilters = selectedFilterChips.length > 0;
  return (
    <div
      ref={rootRef}
      className="relative z-[90] overflow-visible rounded-xl border border-[#E8E8E0] bg-white shadow-sm"
    >
      <div className="rounded-xl border-b border-[#ECECE3] bg-gradient-to-r from-[#FFF0E4] via-[#FFF8F1] to-[#F3F6FF] px-3 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {vendorTypes.map((vendorType) => (
                <button
                  key={vendorType}
                  ref={(node) => {
                    vendorTypeButtonRefs.current[vendorType] = node;
                  }}
                  onClick={() => handleVendorTypeClick(vendorType)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                    selectedVendorType === vendorType
                      ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                      : "border-[#DADAD0] bg-white text-[#555548] hover:border-[#E95420]"
                  }`}
                >
                  {vendorType}
                  <span className="ml-1 text-gray-500">
                    ({vendorTypeCounts[vendorType] || 0})
                  </span>
                </button>
              ))}
            </div>

            {hasAppliedFilters && (
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="shrink-0 text-[11px] text-gray-600">
                  Applied Filters:
                </span>

                {selectedFilterChips.map((chip) => (
                  <div
                    key={`${chip.type}-${chip.id}`}
                    className="flex items-center gap-1 rounded-full border border-[#F7B99D] bg-[#FFF4EE] px-2 py-[2px] text-[11px] text-[#B43A12]"
                  >
                    <span>{chip.label}</span>
                    <button
                      type="button"
                      onClick={() => handleChipRemove(chip)}
                      className="font-bold leading-none hover:text-red-500"
                    >
                      x
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="shrink-0 text-[11px] font-semibold text-[#E95420] hover:underline"
                  onClick={() => {
                    setIsExpanded(false);
                    setSearchText("");
                    clearAllVendorCategoryFilters();
                  }}
                >
                  Clear All
                </button>
              </div>
            )}
          </div>

           {selectedVendorType && (
              <input
                type="text"
                value={searchText}
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchText(value);
                  if (value.trim()) {
                    setIsExpanded(true);
                  }
                }}
                onFocus={() => {
                  if (searchText.trim()) {
                    setIsExpanded(true);
                  }
                }}
                placeholder={
                  selectedVendorType === "Material"
                    ? "Search discipline, category, subcategory, or make/brand"
                    : "Search discipline, category, or subcategory"
                }
                className="min-w-[200px] max-w-[300px] flex-1 rounded-lg border border-[#DADAD0] bg-white px-3 py-2 text-xs text-gray-700 placeholder:text-gray-400 focus:border-[#E95420] focus:outline-none focus:ring-0"
              />
            )}
        </div>
      </div>

      {selectedVendorType && (
        <Grow
          key={selectedVendorType}
          in={showDetailPanels}
          timeout={220}
          mountOnEnter
          unmountOnExit
          style={{ transformOrigin: `${panelOriginX}px 0px` }}
        >
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-[120] rounded-xl border border-[#DEDCD2] bg-white shadow-[0_22px_55px_rgba(26,26,18,0.24)]">
        <div className="p-3">
          <div className="mb-3 flex items-start justify-between gap-3 px-1">
            <div>
              <h3 className="text-sm font-semibold text-[#20201A]">
                Select Vendor Categories
              </h3>
              <p className="mt-0.5 text-[11px] text-gray-500">
                Choose a discipline, category, sub-category and make to filter vendors
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              aria-label="Close category filters"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-lg leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            >
              ×
            </button>
          </div>
          <div
            className={`grid gap-2 ${selectedVendorType === "Material" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}
          >
          <div className="flex h-[270px] min-w-0 flex-col rounded-lg border border-[#E8E8E0] bg-white">
            <div className="flex items-center justify-between border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                  1
                </span>
                Discipline
              </span>
              {selectedDisciplineId && (
                <button
                  type="button"
                  onClick={() =>
                    applyQuickFilter({ vendorType: selectedVendorType })
                  }
                  className="text-[10px] font-medium text-[#4D6C93] hover:underline"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="flex-1 space-y-1 overflow-y-auto p-2">
              {visibleDisciplines.length > 0 ? (
                visibleDisciplines.map((discipline) => (
                  <button
                    key={discipline._id}
                    onClick={() => handleDisciplineClick(discipline._id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors ${
                      selectedDisciplineId === discipline._id
                        ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                        : "border-transparent bg-white text-gray-700 hover:border-[#E5E3DA] hover:bg-[#FAFAF7]"
                    }`}
                    title={
                      normalizedSearchText && discipline?.name
                        ? `Discipline: ${discipline.name}`
                        : undefined
                    }
                  >
                    <span className="min-w-0 truncate font-medium">
                      {discipline.name}
                    </span>
                    <span className="flex shrink-0 items-center gap-2 text-gray-500">
                      {disciplineCounts[discipline._id] || 0}
                      <ChevronRightIcon
                        aria-hidden="true"
                        sx={{ fontSize: 20 }}
                        className="text-[#66665D]"
                      />
                    </span>
                  </button>
                ))
              ) : (
                <div className="text-xs text-gray-500">
                  No disciplines available for this main category
                </div>
              )}
            </div>
          </div>

          <div className="flex h-[270px] min-w-0 flex-col rounded-lg border border-[#E8E8E0] bg-white">
            <div className="flex items-center justify-between border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                  2
                </span>
                Categories
              </span>
              {selectedCategoryId && (
                <button
                  type="button"
                  onClick={() =>
                    applyQuickFilter({
                      vendorType: selectedVendorType,
                      taxonomyId: selectedDisciplineId,
                    })
                  }
                  className="text-[10px] font-medium text-[#4D6C93] hover:underline"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {selectedDisciplineId ? (
                <div className="space-y-1">
                  {visibleCategories.length > 0 ? (
                    visibleCategories.map((category) => (
                      <button
                        key={category._id}
                        onClick={() => handleCategoryClick(category._id)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors ${
                          selectedCategoryId === category._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-transparent bg-white text-gray-700 hover:border-[#E5E3DA] hover:bg-[#FAFAF7]"
                        }`}
                        title={
                          normalizedSearchText
                            ? [
                                category?.disciplineName &&
                                  `Discipline: ${category.disciplineName}`,
                                category?.name && `Category: ${category.name}`,
                              ]
                                .filter(Boolean)
                                .join(" | ")
                            : undefined
                        }
                      >
                        <span className="min-w-0 truncate font-medium">
                          {category.name}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-gray-500">
                          {categoryCounts[category._id] || 0}
                          <ChevronRightIcon
                            aria-hidden="true"
                            sx={{ fontSize: 20 }}
                            className="text-[#66665D]"
                          />
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No categories available for this discipline
                    </div>
                  )}
                </div>
              ) : normalizedSearchText ? (
                <div className="space-y-1">
                  {visibleCategories.length > 0 ? (
                    visibleCategories.map((category) => (
                      <button
                        key={category._id}
                        onClick={() => handleCategoryClick(category._id)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors ${
                          selectedCategoryId === category._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-transparent bg-white text-gray-700 hover:border-[#E5E3DA] hover:bg-[#FAFAF7]"
                        }`}
                        title={
                          category?.disciplineName
                            ? `Discipline: ${category.disciplineName}`
                            : undefined
                        }
                      >
                        <span className="min-w-0 truncate font-medium">
                          {category.name}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-gray-500">
                          {categoryCounts[category._id] || 0}
                          <ChevronRightIcon
                            aria-hidden="true"
                            sx={{ fontSize: 20 }}
                            className="text-[#66665D]"
                          />
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No categories match your search
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  Select a discipline to view categories
                </div>
              )}
            </div>
          </div>

          <div className="flex h-[270px] min-w-0 flex-col rounded-lg border border-[#E8E8E0] bg-white">
            <div className="flex items-center justify-between border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                  3
                </span>
                Sub Categories
              </span>
              {selectedSubCategoryId && (
                <button
                  type="button"
                  onClick={() =>
                    applyQuickFilter({
                      vendorType: selectedVendorType,
                      taxonomyId: selectedCategoryId,
                    })
                  }
                  className="text-[10px] font-medium text-[#4D6C93] hover:underline"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {selectedCategoryId ? (
                <div className="space-y-1">
                  {visibleSubCategories.length > 0 ? (
                    visibleSubCategories.map((subCategory) => (
                      <button
                        key={subCategory._id}
                        onClick={() => handleSubCategoryClick(subCategory._id)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors ${
                          selectedSubCategoryId === subCategory._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-transparent bg-white text-gray-700 hover:border-[#E5E3DA] hover:bg-[#FAFAF7]"
                        }`}
                        title={
                          normalizedSearchText
                            ? [
                                subCategory?.categoryName &&
                                  `Category: ${subCategory.categoryName}`,
                                subCategory?.name &&
                                  `Sub Category: ${subCategory.name}`,
                              ]
                                .filter(Boolean)
                                .join(" | ")
                            : undefined
                        }
                      >
                        <span className="min-w-0 truncate font-medium">
                          {subCategory.name}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-gray-500">
                          {subCategoryCounts[subCategory._id] || 0}
                          <ChevronRightIcon
                            aria-hidden="true"
                            sx={{ fontSize: 20 }}
                            className="text-[#66665D]"
                          />
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No sub categories available for this category
                    </div>
                  )}
                </div>
              ) : normalizedSearchText ? (
                <div className="space-y-1">
                  {visibleSubCategories.length > 0 ? (
                    visibleSubCategories.map((subCategory) => (
                      <button
                        key={subCategory._id}
                        onClick={() => handleSubCategoryClick(subCategory._id)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors ${
                          selectedSubCategoryId === subCategory._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-transparent bg-white text-gray-700 hover:border-[#E5E3DA] hover:bg-[#FAFAF7]"
                        }`}
                        title={
                          subCategory?.categoryName
                            ? `Category: ${subCategory.categoryName}`
                            : undefined
                        }
                      >
                        <span className="min-w-0 truncate font-medium">
                          {subCategory.name}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-gray-500">
                          {subCategoryCounts[subCategory._id] || 0}
                          <ChevronRightIcon
                            aria-hidden="true"
                            sx={{ fontSize: 20 }}
                            className="text-[#66665D]"
                          />
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No sub categories match your search
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  Select a category to view subcategories
                </div>
              )}
            </div>
          </div>

          {selectedVendorType === "Material" && (
            <div className="flex h-[270px] min-w-0 flex-col rounded-lg border border-[#E8E8E0] bg-white">
              <div className="flex items-center justify-between border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                  <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                    4
                  </span>
                  Makes / Brands
                </span>
                {selectedMakeId && (
                  <button
                    type="button"
                    onClick={() =>
                      applyQuickFilter({
                        vendorType: selectedVendorType,
                        taxonomyId: selectedSubCategoryId,
                      })
                    }
                    className="text-[10px] font-medium text-[#4D6C93] hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                {!selectedSubCategoryId && !normalizedSearchText ? (
                  <div className="text-xs text-gray-500">
                    Select a subcategory to view makes / brands
                  </div>
                ) : (normalizedSearchText
                    ? searchMakeLookup.status
                    : makeLookup.status) === "loading" ? (
                  <div className="text-xs text-gray-500">
                    Searching makes / brands...
                  </div>
                ) : (normalizedSearchText
                    ? searchMakeLookup.status
                    : makeLookup.status) === "error" ? (
                  <div className="text-xs text-red-600">
                    Unable to load makes / brands.{" "}
                    <button
                      type="button"
                      onClick={() => {
                        if (normalizedSearchText) {
                          setSearchMakeLookup({
                            key: "",
                            status: "idle",
                            bySubCategory: {},
                          });
                        } else {
                          setMakeRetry((value) => value + 1);
                        }
                      }}
                      className="font-semibold underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : (normalizedSearchText
                    ? matchingMakes
                    : makeLookup.options).length > 0 ? (
                  <div className="space-y-1">
                    {(normalizedSearchText
                      ? matchingMakes
                      : makeLookup.options
                    ).map((make) => {
                      const makeId = String(make?.makeId || make?._id || "");
                      const makeSubCategoryId =
                        make?.subCategoryId || selectedSubCategoryId;
                      const scopedMakeCount =
                        makeCountsBySubCategory?.[makeSubCategoryId]?.[makeId];

                      return (
                        <button
                          key={`${makeSubCategoryId}-${makeId}`}
                          type="button"
                          onClick={() =>
                            handleMakeClick(makeId, makeSubCategoryId)
                          }
                          className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors ${
                            String(selectedMakeId) === makeId &&
                            String(selectedSubCategoryId) ===
                              String(makeSubCategoryId)
                              ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                              : "border-transparent bg-white text-gray-700 hover:border-[#E5E3DA] hover:bg-[#FAFAF7]"
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium">
                              {make.name}
                            </span>
                            {normalizedSearchText && make.subCategoryName && (
                              <span className="block truncate text-[9px] text-gray-400">
                                {make.subCategoryName}
                              </span>
                            )}
                          </span>
                          <span className="flex shrink-0 items-center gap-2 text-gray-500">
                            {scopedMakeCount ?? makeCounts[makeId] ?? 0}
                            <ChevronRightIcon
                              aria-hidden="true"
                              sx={{ fontSize: 20 }}
                              className="text-[#66665D]"
                            />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    {normalizedSearchText
                      ? "No makes / brands match your search"
                      : "No makes / brands available for this subcategory"}
                  </div>
                )}
              </div>
            </div>
          )}
          </div>

          {/* <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[#ECECE3] px-1 pt-3">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold text-gray-500">
                Selected Path:
              </span>
              {selectedFilterChips.length > 0 ? (
                selectedFilterChips.map((chip, index) => (
                  <React.Fragment key={`path-${chip.type}-${chip.id}`}>
                    {index > 0 && (
                      <span className="text-[10px] text-gray-300">›</span>
                    )}
                    <span className="rounded-full border border-[#F7C4AC] bg-[#FFF6F1] px-2 py-0.5 text-[10px] text-[#B43A12]">
                      {chip.label}
                    </span>
                  </React.Fragment>
                ))
              ) : (
                <span className="text-[10px] text-gray-400">
                  Choose a vendor category to begin
                </span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className="text-[11px] font-semibold text-gray-700">
                {totalCount} matching vendors
              </span>
              {hasAppliedFilters && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchText("");
                    clearAllVendorCategoryFilters();
                  }}
                  className="text-[11px] font-semibold text-[#E95420] hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
          </div> */}
        </div>
        </div>
        </Grow>
      )}

    </div>
  );
};

export default VendorCategoryQuickFilter;
